import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from '../Spinner/index.js';
import styles from './Button.module.css';

export type ButtonKind = 'primary' | 'brand' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Visual kind. Default 'primary' (filled brand). */
  kind?: ButtonKind;
  /** Size. Default 'md'. */
  size?: ButtonSize;
  /** Optional leading icon. Replaced by Spinner when `loading` is true. */
  leadingIcon?: ReactNode;
  /** Optional trailing icon. */
  trailingIcon?: ReactNode;
  /** When true, shows the spinner in place of the leading icon and disables interaction. */
  loading?: boolean;
  /** When true, the button takes the full width of its container. */
  fullWidth?: boolean;
  /** Button content (label). */
  children: ReactNode;
}

/**
 * Standard button. Five kinds × five states × three sizes.
 *
 * - `primary` — filled brand, the most-important action per surface
 * - `brand` — same family as primary; reserved for marketing-adjacent emphasis
 * - `secondary` — outlined; equally-weighted alternative
 * - `ghost` — transparent; tertiary actions, toolbar buttons
 * - `danger` — destructive actions
 *
 * Loading state replaces the leading icon with a spinner (never appends).
 * Click is disabled while loading. The full-width variant is intended for
 * form submit buttons inside Card-bound forms (auth screens).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    kind = 'primary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    loading = false,
    fullWidth = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;

  const classes = [
    styles.button,
    styles[`kind_${kind}`],
    styles[`size_${size}`],
    fullWidth ? styles.fullWidth : null,
    loading ? styles.loading : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Spinner color follows the button's text color via currentColor.
  const spinnerSize: 'sm' | 'md' = size === 'sm' ? 'sm' : 'md';

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {(loading || leadingIcon) && (
        <span className={styles.leading} aria-hidden={loading ? true : undefined}>
          {loading ? <Spinner size={spinnerSize} /> : leadingIcon}
        </span>
      )}
      <span className={styles.label}>{children}</span>
      {trailingIcon && !loading && (
        <span className={styles.trailing} aria-hidden="true">
          {trailingIcon}
        </span>
      )}
    </button>
  );
});
